import { ChevronDownIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Switch,
    Text,
    Tooltip,
    useToast,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useDisclosure,
    RadioGroup,
    Stack,
    Radio,
    Card,
    CardBody,
    useColorModeValue
} from '@chakra-ui/react';
import CustomDeepLinkInput from 'components/genericForms/CustomDeepLinkInput';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useGetAdvertisableAppsQuery } from 'store/campaigns/adsetFormApi';
import { useUpdatePublishAdMutation } from 'store/publishTracking/trackingApi';
import {
    call_to_action_options
} from 'utils/callToActions';
import { removeEmptyValues } from 'utils/functions';
import { object, string } from "yup";
import BuildUrlParamsUpdate from '../BuildUrlParamsUpdate';
import FaceBookPageSelector from '../FaceBookPageSelector';
import InstagramAccountSelector from '../InstagramAccountSelector';
import UrlParametersUpdate from '../UrlParametersUpdate';
import PublishCatalogueAdPreviews from './PublishCatalogueAdPreviews';
import CatalogueFrameForSingle from 'components/genericForms/CatalogueFrameForSingle';


const CatalogueCarouselPublishAdUpdate = ({ trackingData, targeting, onClose, onSaveSuccess }) => {
    const { adsAccount, form, _id, adset, campaign } = trackingData;
    const toast = useToast();
    const background = useColorModeValue('gray.50', 'gray.800');
    const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const [isEnabled, setIsEnabled] = useState(false);
    const [isUrlModalOpen, setUrlModalOpen] = useState(false);
    const [isUrlParamModalOpen, setUrlParamModalOpen] = useState(false);
    const { isOpen: isCreativeModalOpen, onOpen: onCreativeModalOpen, onClose: onCreativeModalClose } = useDisclosure();

    const [updatePublishAd] = useUpdatePublishAdMutation();

    const pageId = adset?.promoted_object?.page_id;

    let pageIdToSend = null;

    if (
        adset?.destination_type === "PHONE_CALL" &&
        pageId
    ) {
        pageIdToSend = pageId;
    }else if (
        campaign?.objective === "OUTCOME_AWARENESS"
    ){
        pageIdToSend = pageId;
    }

    const { data: advertisableApps } = useGetAdvertisableAppsQuery({adsAccountId: adsAccount.adsAccountId}, {skip: !adsAccount.adsAccountId});

    const mustStartWithHttpRegex = /^https?:\/\/.+$/;

    // Validation Schema
    const validationSchema = object().shape({
        // name: string().required('Ad Name is required'),
        identity: object().shape({
            page_id: string().required('Facebook Page is required'),
        }),
        type: string().required('Type is required'),
        link: string().required('URL is required'),
        template_url_spec: object().shape({
            android: object().shape({
                url: string()
                .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
            }),
            ios: object().shape({
                url: string()
                .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
            }),
            web: object().shape({
                url: string()
                .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
            }),
    
        })
    });

    const initialValues = {
        name: form.name || "",
        cpa: form.cpa || "",
        identity: {
            page_id: form?.identity?.page_id || "",
            instagram_user_id: form?.identity?.instagram_user_id || "",
        },
        adType: "CAROUSEL",
        _id: form?.adcreative[0]?._id || "",
        message: form?.message || "",
        url_tags: form?.url_tags || '',
        headline: form?.adcreative[0]?.name || form?.name  || "",
        description: form?.description || "",
        type: form?.type || "",
        link: form?.link || "",
        caption: form?.caption || '',
        product_set_id: form?.adcreative[0]?.product_set_id ,
        url: form?.url || "",
        applink_treatment: form?.applink_treatment || "web_only",
        template_url_spec: {
            web: {
                url: form?.template_url_spec?.web?.url || '',
            },
            config: {
                app_id: form?.template_url_spec?.config?.app_id || ''
            },
            android: {
                url: form?.template_url_spec?.android?.url || '',
            },
            ios: {
                url: form?.template_url_spec?.ios?.url || '',
            },
        },
        image_layer_specs: form?.adcreative?.[0]?.image_layer_specs || [],
        format_option: form?.adcreative?.[0]?.format_option || 'carousel_images_multi_items',        
        override_catalog: form?.template_url_spec?.web?.url ? true : false || false,
        enroll_status: form?.contextual_multi_ads?.enroll_status ? form?.contextual_multi_ads?.enroll_status : 'OPT_OUT',
        force_single_link: false,
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {

            const templateUrlSpec =
                values.applink_treatment === 'web_only'
                    ? {
                        web: {
                            url: values.template_url_spec?.web?.url || '',
                        },
                    }
                    : {
                        web: {
                            url: values.template_url_spec?.web?.url || '',
                        },
                        ios: {
                            url: values.template_url_spec?.ios?.url || '',
                        },
                        android: {
                            url: values.template_url_spec?.android?.url || '',
                        },
                        config: {
                            app_id: values.template_url_spec?.config?.app_id || '',
                        },
                    };

            
            const filtered_image_layer_specs = values.image_layer_specs?.map(layer => {
                // Create a shallow copy of the object without frame_name and frame_url
                const { frame_name, frame_url, ...rest } = layer;
                return rest;
            }) || [];

            const payload = {
                name: values.name,
                cpa: values.cpa,
                identity: {
                    page_id: values.identity.page_id,
                    ...(values?.identity?.instagram_user_id.trim() && {
                        instagram_user_id: values?.identity?.instagram_user_id,
                    }),
                },
                contextual_multi_ads: {
                    enroll_status: values?.enroll_status,
                },
                asset_feed_spec: {
                    ad_formats: [
                        "CAROUSEL",
                        "COLLECTION"
                    ],
                    optimization_type: "FORMAT_AUTOMATION",
                },
                template_url_spec: templateUrlSpec,
                message: values.message,
                description: values.description,
                type: values.type,
                link: values.link,
                caption: values.caption,
                ...(values.url_tags?.trim() && { url_tags: values.url_tags }),
                adcreative: [
                    {
                        _id: values._id,
                        name: values.headline,
                        description: "",
                        type: values.type,
                        link: "https://docs.google.com/spreadsheets/d/1/",
                        product_set_id: values.product_set_id,
                        url:  "",
                        format_option: values.format_option,
                        ...(filtered_image_layer_specs.length > 0 && values.format_option === 'carousel_images_multi_items' &&  {
                            image_layer_specs: filtered_image_layer_specs,
                        }),
                    }
                ]
            };

            const filteredPayload = removeEmptyValues(payload);

            updatePublishAd({ trackingId: _id, payload: filteredPayload })
                .unwrap()
                .then(response => {
                    onSaveSuccess();
                    toast({
                        position: "top-right",
                        title: "Ad updated",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .catch(error => {
                    toast({
                        position: "top-right",
                        title: "Error updating ad",
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setSubmitting(false);
                    onClose();
                });
        },
    });

    const handleUrlChange = (constructedUrl) => {
        formik.setFieldValue('link', constructedUrl);
    };

    const handleUrlParamChange = (constructedUrl) => {
        formik.setFieldValue('url_tags', constructedUrl);
    };

    if (!trackingData || !formik.values) {
        return <Spinner mt={'20px'} />;
    }

    return (
        <form onSubmit={formik.handleSubmit}>
            <Flex gap={4}>
                <Flex direction={'column'} flex={0.4}>
                    <Flex direction={'column'} gap={4} borderRadius="md" bg={background} p={4} mb={4}>
                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold" fontSize={'14px'}>Ad name</Text>
                            <Input
                                name="name"
                                placeholder="Enter ad name"
                                size='sm'
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.name}</Text>
                            )}
                        </Flex>

                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold" fontSize={'14px'}>Facebook page</Text>
                            <FaceBookPageSelector
                                adsAccount={{
                                    account_id: adsAccount.adsAccountId,
                                    name: adsAccount.adsAccountName
                                }}
                                pageIdToSend={pageIdToSend ? pageIdToSend : ''}
                                selectedPageId={formik?.values?.identity?.page_id}
                                onSelectedPage={(pageId) => {
                                    formik.setFieldValue('identity.page_id', pageId);

                                    // Reset the Instagram actor ID to an empty string
                                    formik.setFieldValue('identity.instagram_user_id', '');
                                }}
                            />
                            {formik.errors.identity?.page_id && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.identity?.page_id}</Text>
                            )}
                        </Flex>

                        {adset?.destination_type === 'ON_PAGE' ? null :
                            <Flex direction="column" gap={1}>
                                <Text fontWeight="bold" fontSize={'14px'}>Instagram accounts</Text>
                                <InstagramAccountSelector
                                    adsAccount={adsAccount}
                                    selectedInstPageId={formik?.values?.identity?.instagram_user_id}
                                    onSelectedPage={(pageId) => {
                                        formik.setFieldValue('identity.instagram_user_id', pageId);
                                    }}
                                    pageID={formik?.values?.identity?.page_id}
                                />
                            </Flex>
                        }
                    </Flex>
                    <Flex direction={'column'} gap={4} borderRadius="md" bg={background} p={4} mb={4}>
                        {/* Multi-advertiser ads Section */}
                        <Flex direction="column" borderRadius="md" gap={2}>
                            <Checkbox
                                onChange={(e) => {
                                    formik.setFieldValue("enroll_status", e.target.checked ? 'OPT_IN' : 'OPT_OUT');
                                }}
                                colorScheme="brand"
                                isChecked={formik.values.enroll_status === 'OPT_IN'}
                            >
                                <Flex align={'center'}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Multi-advertiser ads &nbsp;</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p="5px">
                                                <Text>Your ads can appear alongside other ads in the same ad unit to help people discover products and services from businesses that are personalized to them. Your ad creative may be resized or cropped to fit the ad unit.</Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                            </Checkbox>
                        </Flex>

                        <Flex direction={'column'} gap={'10px'}>
                            <Flex direction="column" gap={1}>
                            <Flex alignItems="center">
                                <Text fontWeight="bold">Destination</Text>
                            </Flex>
                            <Flex alignItems="center" gap={2}>
                                <Text fontWeight="bold" fontSize={'14px'}>Default destination</Text>
                                <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                    <Text>
                                        Send people to either your app or website URL.
                                    </Text>
                                    </Box>
                                }
                                fontSize="md">
                                <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                            <Select
                                name="applink_treatment"
                                bg="#fff"
                                size="sm"
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.applink_treatment}
                            >
                                <option value="web_only">Website</option>
                                <option value="deeplink_with_web_fallback">App</option>
                            </Select>
                            </Flex>

                            {formik.values.applink_treatment === 'deeplink_with_web_fallback' ?
                            <>
                                <Flex direction={'column'} gap={1} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Mobile app</Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p="5px">
                                        <Text>Only select a mobile app if you are using a universal link (iOS or Android app link).</Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <Select
                                    name={`template_url_spec.config.app_id`}
                                    bg="#fff"
                                    size="sm"
                                    borderRadius="md"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik?.values?.template_url_spec?.config?.app_id}
                                >
                                    <option value="">Choose app</option>
                                    {advertisableApps?.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </Select>
                                </Flex>

                                <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">iOS deep link</Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="14px" p="5px">
                                        <Text>
                                            Use a deep link to specify where in your app to send people. Entering an iOS deep link here will override any iOS deep links for items in the selected catalog.
                                        </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={formik.values?.template_url_spec?.ios?.url}
                                    onChange={(url) => {
                                    formik.setFieldValue('template_url_spec.ios.url', url)
                                    }}
                                    placeholder={'Enter the deep link URL'}
                                />
                                </Flex>

                                <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Android deep link</Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="14px" p="5px">
                                        <Text>
                                            Use a deep link to specify where in your app to send people. Entering an Android deep link here will override any Android deep links for items in the selected catalog.
                                        </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={formik.values?.template_url_spec?.android?.url}
                                    onChange={(url) => formik.setFieldValue('template_url_spec.android.url', url)}
                                    error={
                                        formik.errors?.template_url_spec?.android?.url
                                    }
                                    placeholder={'Enter the deep link URL'}
                                />
                                </Flex>
                            </>
                            : null}

                            <Flex direction="column" gap={1} mt={'10px'}>
                                <Flex alignItems="center">
                                    <Text fontWeight="bold" fontSize={'14px'}>Website URL</Text>
                                    <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p="5px">
                                        <Text>Enter the URL for the webpage you want people to visit</Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <Input
                                    name="link"
                                    placeholder={'http://www.example.com/page'}
                                    size='sm'
                                    borderRadius="md"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.link}
                                />
                             <Flex mt={2}>
                                    <Button onClick={() => {
                                        setUrlModalOpen(true)
                                    }}
                                        variant="link"
                                        color='brand'>
                                        <Text fontSize={'12px'} >{'Build a URL parameter'}</Text>
                                    </Button>
                            </Flex>
                            {formik.touched.link && formik.errors.link && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.link}</Text>
                            )}
                            </Flex>

                            <Flex direction="column" gap={1} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Display link</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p="5px">
                                                <Text>Show a short version of your website URL instead of your full URL. The domains for your website URL and display link should match.</Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <Input
                                    name="caption"
                                    placeholder="Enter link to show on last carousel card"
                                    bg="#fff"
                                    size='sm'
                                    borderRadius="md"
                                    value={formik.values.caption}
                                    onChange={formik.handleChange}
                                />
                            </Flex>

                            <Flex direction="column" mb={2} borderRadius="md" gap={2} mt={'10px'}>
                            <Checkbox
                                onChange={(e) => {
                                formik.setFieldValue("override_catalog", e.target.checked);
                                }}
                                colorScheme="brand"
                                isChecked={formik.values.override_catalog}
                                alignItems="flex-start"
                            >
                                <Flex direction="column">
                                <Flex align="center" mt={'-3px'}>
                                    <Text fontWeight="bold" fontSize="14px">
                                        Override catalog website deep links&nbsp;
                                    </Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p="5px">
                                        <Text>
                                            Link each item in your catalog to a specific page on your website.
                                            You can add deep links in your catalog's data feed. Use a deep link
                                            to specify where in your website to send people. Entering a deep link
                                            to website here will override any website deep links for items in the
                                            selected catalog.
                                        </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                {/* Description text on the next line */}
                                <Text fontSize="11px">
                                    This allows you to override any website deep links for items in the selected catalog.
                                </Text>
                                </Flex>
                            </Checkbox>
                            {formik.values.override_catalog ?
                                <>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Set catalog deep links</Text>
                                    <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="14px" p="5px">
                                        <Text>
                                            Enter the URL for the website deep link you want people to visit. This URL will override the destination links in your catalog.
                                        </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                    >
                                    <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>

                                <CustomDeepLinkInput
                                    value={formik.values?.template_url_spec?.web?.url}
                                    onChange={(url) => formik.setFieldValue('template_url_spec.web.url', url)}
                                    error={
                                        formik.touched?.template_url_spec?.web?.url &&
                                        formik.errors?.template_url_spec?.web?.url
                                    }
                                    placeholder={'http://www.example.com/page'}
                                />

                                </>
                                : null}
                            </Flex>
                        </Flex>

                        <Flex direction={'column'} gap={1}>
                            <Text fontWeight="bold">Ad Creative</Text>
                            <Text fontSize={'12px'}>Select and optimize your ad text, media and enhancements.</Text>
                        </Flex>

                        <Flex align="center" wrap="wrap" mb={2} gap={2}>
                            <Text fontWeight="bold" fontSize={'14px'}>Creative options</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                <Box fontSize="12px" p="5px">
                                    <Text>
                                        Choose a creative format for your carousel cards. The slideshow format will default to a single image if you don't provide more than one image per item.
                                    </Text>
                                </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                         </Flex>

                        <Flex mb={2}>
                            <RadioGroup
                                name="format_option"
                                value={formik.values.format_option}
                                onChange={(value) => {
                                    formik.setFieldValue('format_option', value);
                                }}
                            >
                                <Stack spacing={5} direction='column'>
                                <Flex direction="column">
                                    <Flex align="center">
                                    <Radio colorScheme='brand' value='carousel_images_multi_items' />
                                    <Text fontWeight="bold" ml={2} fontSize={'14px'}>Single image</Text>
                                    </Flex>
                                    <Text fontSize={'12px'} ml={6}>Show one image per card for each item in your catalog.</Text>
                                </Flex>

                                <Flex direction="column">
                                    <Flex align="center">
                                    <Radio colorScheme='brand' value='carousel_slideshows' />
                                    <Text fontWeight="bold" ml={2} fontSize={'14px'}>Slideshow </Text>
                                    </Flex>
                                    <Text fontSize={'12px'} ml={6}>Create a looping video for each card using multiple images per item.</Text>
                                </Flex>
                                </Stack>
                            </RadioGroup>
                        </Flex>

                        {(formik.values.format_option === 'carousel_slideshows') ?
                                <Card 
                                    borderRadius="5px" 
                                    p={2}
                                    borderLeft={"4px solid"}
                                    borderColor={"gray.100"}
                                    mb={2}
                                >
                                    <CardBody as={Flex} align="flex-start" gap={2} p={1}>
                                        <InfoOutlineIcon style={{ pointerEvents: 'all' }} />
                                        <Text fontSize="12px">
                                            Make sure you have multiple images for each item in your product set. When multiple images aren't available, a single image shows instead.
                                        </Text>
                                    </CardBody>
                                </Card>
                            :
                                <>
                                    <Flex align="center" wrap="wrap" mb={2} gap={2}>
                                        <Text fontWeight="bold" fontSize={'14px'}>Creative tools</Text>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                            <Box fontSize="12px" p="5px">
                                                <Text>
                                                Customise how images from your catalogue appear in your ads. Frame products and services with engaging graphics and add helpful info from your catalogue, such as price or free delivery.
                                                </Text>
                                            </Box>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" />
                                        </Tooltip>
                                    </Flex>
                                    <Flex mb={2}>
                                        <Menu>
                                        <MenuButton
                                            as={Button}
                                            variant="outline"
                                            fontWeight="normal"
                                            bg={inputBackground}
                                            _active={{ background: inputBackground, borderColor: "blue.500" }}
                                            _hover={{ background: inputBackground, borderColor: "gray.300" }}
                                            rightIcon={<ChevronDownIcon boxSize={5} />}
                                            size="sm"
                                        >
                                        Edit creative
                                        </MenuButton>
                                        <MenuList minW="135px" px={2} py={1}>
                                        <MenuItem
                                            fontSize="14px"
                                            onClick={onCreativeModalOpen}
                                        >
                                            Add a frame
                                        </MenuItem>
                                        </MenuList>
                                        </Menu>
                                    </Flex>
                                </>
                        }


                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold" fontSize={'14px'}>Primary text</Text>
                             <CustomDeepLinkInput
                                value={formik.values?.message}
                                onChange={(message) => formik.setFieldValue('message', message)}
                                error={
                                    formik.touched?.message &&
                                    formik.errors?.message
                                }
                                placeholder={'Tell people what your ad is about'}
                            />
                        </Flex>
                        
                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold" fontSize={'14px'}>Headline</Text>
                             <CustomDeepLinkInput
                                value={formik.values.headline}
                                onChange={(headline) => formik.setFieldValue('headline', headline)}
                                error={
                                    formik.touched.headline &&
                                    formik.errors.headline
                                }
                                placeholder={'Add an attention-grabbing headline'}
                            />
                        </Flex>  

                        {/* <Flex direction="column" gap={1}>
                            <Text fontWeight="bold">Description</Text>
                            <Textarea
                                name="description"
                                placeholder="Enter description"
                                size='sm'
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                            />
                        </Flex> */}

                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold" fontSize={'14px'}>Call to action</Text>
                            <Select
                                name="type"
                                size="sm"
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.type}
                            >
                                {call_to_action_options.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>

                            {formik.touched.type && formik.errors.type && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.type}</Text>
                            )}
                        </Flex>
                    </Flex>

                    {['PHONE_CALL', 'ON_PAGE', 'APP', 'WHATSAPP'].includes(adset?.destination_type) ? null :
                    <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                        <Text fontWeight={'bold'}>Tracking</Text>
                        <Flex direction="column" gap={1}>
                            <Flex alignItems={'center'}>
                                <Text mb={2} fontWeight="bold" fontSize={'14px'}>
                                    URL parameters{" "}
                                    {/* <Text as="span" fontWeight="bold" color={'gray.600'} mr={1}>.</Text> */}
                                    <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                </Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p={'5px'}>
                                            <Text >
                                                Add parameters to the end of your website URL to track where your visitors are coming from.
                                            </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                </Tooltip>
                            </Flex>
                            <Input
                                name="url_tags"
                                placeholder="key1=value1&key2=value2"
                                bg="#fff"
                                size='sm'
                                borderRadius="md"
                                value={formik.values.url_tags}
                                onChange={formik.handleChange}
                            />
                            <Flex mt={2}>
                                <Button onClick={() => {
                                    setUrlParamModalOpen(true)
                                }}
                                    variant="link"
                                    color='brand'>
                                    <Text fontSize={'12px'} >{'Build a URL parameter'}</Text>
                                </Button>
                            </Flex>
                            {formik.touched.url_tags && formik.errors.url_tags && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.url_tags}</Text>
                            )}
                        </Flex>
                    </Flex>
                    }

                    <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold">CPA</Text>
                            <Input
                                name="cpa"
                                placeholder="Enter CPA"
                                size='sm'
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.cpa}
                            />
                        </Flex>
                    </Flex>

                    <Flex mb={2}>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            isLoading={formik.isSubmitting}
                            type="submit"
                        >
                            Save
                        </Button>
                    </Flex>
                </Flex>

                <Flex flex={0.6} px={4} direction={'column'} justifyContent={'space-between'} borderRadius={'md'} boxShadow="0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)">
                    <Flex py={2} gap={2} direction={'column'}>
                        <Flex py={2} gap={2}>
                            <Switch isChecked={isEnabled} colorScheme='brand' onChange={() => setIsEnabled(prevState => !prevState)} />
                            <Text fontWeight={'bold'} fontSize={'14px'}>Ad preview  </Text>
                        </Flex>
                        <Divider />
                        {isEnabled &&
                            <PublishCatalogueAdPreviews placements={targeting} file={formik.values} adsAccountData={adsAccount} />
                        }
                    </Flex>

                    <Flex my={2} alignItems={'center'}>
                        <Text fontSize={'12px'} mr={2}>Ad rendering and interaction may vary based on device, format and other factors.</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                                <Box fontSize="12px" p="5px">
                                    <Text>
                                        Ads Manager previews are just examples and are not a substitute for reviewing Help Centre guidance and testing formats and devices to ensure that your ad delivers as intended.
                                    </Text>
                                </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" boxSize={3} />
                        </Tooltip>
                    </Flex>
                </Flex>
            </Flex>

            {/* Modal for "Add a frame" */}
            {isCreativeModalOpen && 
                <CatalogueFrameForSingle 
                    isOpen={isCreativeModalOpen}
                    onClose={onCreativeModalClose}
                    productSetId={formik.values.product_set_id}
                    selectedAdAccounts={[{
                        adsAccountId: adsAccount?.adsAccountId,
                        adsAccountName: adsAccount?.adsAccountName,
                    }]}
                    values={formik.values}
                    onChangeImageLayerSpecs={(framesData) => {
                        formik.setFieldValue(
                            "image_layer_specs",
                            framesData
                    )}}
                />
            }

            <Modal isOpen={isUrlModalOpen} onClose={() => setUrlModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BuildUrlParamsUpdate link={formik.values.adcreative} onClose={() => setUrlModalOpen(false)} onUrlChange={handleUrlChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isUrlParamModalOpen} onClose={() => setUrlParamModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <UrlParametersUpdate link={formik.values.adcreative} onClose={() => setUrlParamModalOpen(false)} onUrlChange={handleUrlParamChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </form>
    );
};

export default CatalogueCarouselPublishAdUpdate;
